import { BUILTIN_VOICE_OPTIONS } from '../pages/settings/voiceConstants';

export type BuiltinVoiceId = (typeof BUILTIN_VOICE_OPTIONS)[number]['id'];

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  ar: 'ar-SA',
  hi: 'hi-IN',
  ur: 'ur-PK',
  bn: 'bn-BD',
  fr: 'fr-FR',
  de: 'de-DE',
  ja: 'ja-JP',
  zh: 'zh-CN',
};

/** Per-voice synthesis tuning so options sound distinct even on a single system voice. */
const VOICE_TUNING: Record<BuiltinVoiceId, { pitch: number; rate: number; voicePatterns: string[] }> = {
  sarah: { pitch: 1.08, rate: 0.92, voicePatterns: ['samantha', 'karen', 'victoria', 'moira', 'fiona', 'female'] },
  michael: { pitch: 0.82, rate: 0.88, voicePatterns: ['daniel', 'alex', 'david', 'fred', 'tom', 'male'] },
  emma: { pitch: 1.14, rate: 1.0, voicePatterns: ['allison', 'ava', 'samantha', 'kate', 'zira', 'female'] },
  james: { pitch: 0.76, rate: 0.84, voicePatterns: ['alex', 'bruce', 'daniel', 'tom', 'male'] },
  sophia: { pitch: 1.12, rate: 1.06, voicePatterns: ['susan', 'zira', 'samantha', 'karen', 'female'] },
};

const FEMALE_HINTS = ['female', 'woman', 'girl', 'samantha', 'karen', 'victoria', 'zira', 'susan', 'moira', 'fiona', 'allison', 'ava', 'kate'];
const MALE_HINTS = ['male', 'man', 'boy', 'alex', 'daniel', 'david', 'fred', 'tom', 'bruce', 'jorge', 'rishi'];

export const getBuiltinVoiceProfile = (selectedVoiceId?: string) => {
  const fallback = BUILTIN_VOICE_OPTIONS[0];
  const match = BUILTIN_VOICE_OPTIONS.find((voice) => voice.id === selectedVoiceId) || fallback;
  const tuning = VOICE_TUNING[match.id] || VOICE_TUNING.emma;
  return { ...match, ...tuning };
};

const languageMatches = (voiceLang: string, targetLang: string) => {
  const normalizedTarget = targetLang.toLowerCase();
  const normalizedVoice = voiceLang.toLowerCase();
  const targetPrimary = normalizedTarget.split('-')[0];
  return normalizedVoice === normalizedTarget
    || normalizedVoice.startsWith(`${targetPrimary}-`)
    || normalizedVoice.startsWith(targetPrimary);
};

const pickVoiceByGender = (
  candidates: SpeechSynthesisVoice[],
  gender: 'Female' | 'Male'
) => {
  const hints = gender === 'Female' ? FEMALE_HINTS : MALE_HINTS;
  return candidates.find((voice) => hints.some((hint) => voice.name.toLowerCase().includes(hint)));
};

export const resolveBrowserVoice = (
  selectedVoiceId: string | undefined,
  lang: string,
  availableVoices: SpeechSynthesisVoice[]
): SpeechSynthesisVoice | null => {
  if (!availableVoices.length) return null;

  const profile = getBuiltinVoiceProfile(selectedVoiceId);
  const locale = LOCALE_MAP[lang] || LOCALE_MAP.en;
  let candidates = availableVoices.filter((voice) => languageMatches(voice.lang, locale));
  if (!candidates.length) {
    candidates = availableVoices.filter((voice) => languageMatches(voice.lang, LOCALE_MAP.en));
  }
  if (!candidates.length) candidates = availableVoices;

  for (const pattern of profile.voicePatterns) {
    const matched = candidates.find((voice) => voice.name.toLowerCase().includes(pattern));
    if (matched) return matched;
  }

  return pickVoiceByGender(candidates, profile.gender) || candidates[0] || null;
};

export const applyBuiltinBrowserVoice = (
  utterance: SpeechSynthesisUtterance,
  selectedVoiceId: string | undefined,
  lang: string
): void => {
  const profile = getBuiltinVoiceProfile(selectedVoiceId);
  utterance.lang = LOCALE_MAP[lang] || LOCALE_MAP.en;
  utterance.pitch = profile.pitch;
  utterance.rate = profile.rate;

  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

  const voices = window.speechSynthesis.getVoices();
  const resolved = resolveBrowserVoice(selectedVoiceId, lang, voices);
  if (resolved) utterance.voice = resolved;
};

export const speakWithBuiltinBrowserVoice = (
  text: string,
  selectedVoiceId: string | undefined,
  lang: string
): void => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);

  const speak = () => {
    applyBuiltinBrowserVoice(utterance, selectedVoiceId, lang);
    window.speechSynthesis.speak(utterance);
  };

  if (window.speechSynthesis.getVoices().length) {
    speak();
    return;
  }

  let spoke = false;
  const speakOnce = () => {
    if (spoke) return;
    spoke = true;
    window.speechSynthesis.removeEventListener('voiceschanged', speakOnce);
    speak();
  };

  window.speechSynthesis.addEventListener('voiceschanged', speakOnce);
  window.setTimeout(speakOnce, 300);
};

/** Preload browser voices on app start so the first test uses the correct voice immediately. */
export const preloadBrowserVoices = (): void => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  window.speechSynthesis.getVoices();
};
